import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from 'modules/state-mgmt/store';
import { useAppDispatch } from 'modules/state-mgmt/store';
import {
  getStepIndexFromPath,
  getSubStepIndexFromPath,
  getNextStepIndex,
  getPreviousStepIndex,
  getNextSubStepIndex,
  getPreviousSubStepIndex,
  getRouteForNavigation,
  isValidStepIndex,
  isValidSubStepIndex,
  WIZARD_STEPS,
  StepKey,
} from '../config';
import { addVisitedWizardStep } from 'modules/state-mgmt/onboarding';

/**
 * Hook for wizard step navigation - provides next/previous navigation functions
 * that automatically determine the correct routes based on the current step and sub-step
 * Now includes skip functionality for conditional navigation
 */
export const useWizardStepNavigation = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get only the state needed for shouldSkip checks
  const profileFieldConstraintsMap = useAppSelector(state => state.cache.profileFieldConstraintsMap);

  // Get current step and sub-step indices from URL
  const currentStepIndex = getStepIndexFromPath(location.pathname);
  const currentSubStepIndex = getSubStepIndexFromPath(location.pathname, currentStepIndex);

  // Helper function to find next non-skipped step/sub-step
  const findNextValidStep = useCallback(
    (startStepIndex: number, startSubStepIndex: number, skipCount: number = 1) => {
      let stepIndex = startStepIndex;
      let subStepIndex = startSubStepIndex;
      let stepsSkipped = 0;

      while (stepsSkipped < skipCount) {
        const currentStep = WIZARD_STEPS[stepIndex];

        // Try to move to next sub-step first
        if (currentStep?.subSteps) {
          const nextSubIndex = getNextSubStepIndex(stepIndex, subStepIndex);

          if (nextSubIndex !== null) {
            subStepIndex = nextSubIndex;
            const subStep = currentStep.subSteps[subStepIndex];

            // Check if this sub-step should be skipped
            if (!subStep.shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
              stepsSkipped++;
              if (stepsSkipped >= skipCount) {
                return { stepIndex, subStepIndex };
              }
            }
            continue;
          }
        }

        // No more sub-steps, try next main step
        const nextStepIndex = getNextStepIndex(stepIndex);
        if (nextStepIndex === null) {
          return null; // No more steps
        }

        stepIndex = nextStepIndex;
        const nextStep = WIZARD_STEPS[stepIndex];

        // If next step has sub-steps, start from first sub-step
        if (nextStep.subSteps) {
          subStepIndex = 0;
          const firstSubStep = nextStep.subSteps[0];

          // Check if this sub-step should be skipped
          if (!firstSubStep.shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
            stepsSkipped++;
            if (stepsSkipped >= skipCount) {
              return { stepIndex, subStepIndex };
            }
          }
        } else {
          subStepIndex = 0;

          // Check if this main step should be skipped
          if (!nextStep.shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
            stepsSkipped++;
            if (stepsSkipped >= skipCount) {
              return { stepIndex, subStepIndex: undefined };
            }
          }
        }
      }

      return null;
    },
    [profileFieldConstraintsMap],
  );

  // Helper function to find previous non-skipped step/sub-step
  const findPreviousValidStep = useCallback(
    (startStepIndex: number, startSubStepIndex: number, skipCount: number = 1) => {
      let stepIndex = startStepIndex;
      let subStepIndex = startSubStepIndex;
      let stepsSkipped = 0;

      while (stepsSkipped < skipCount) {
        const currentStep = WIZARD_STEPS[stepIndex];

        // Try to move to previous sub-step first
        if (currentStep?.subSteps) {
          const prevSubIndex = getPreviousSubStepIndex(stepIndex, subStepIndex);

          if (prevSubIndex !== null) {
            subStepIndex = prevSubIndex;
            const subStep = currentStep.subSteps[subStepIndex];

            // Check if this sub-step should be skipped
            if (!subStep.shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
              stepsSkipped++;
              if (stepsSkipped >= skipCount) {
                return { stepIndex, subStepIndex };
              }
            }
            continue;
          }
        }

        // No more sub-steps, try previous main step
        const prevStepIndex = getPreviousStepIndex(stepIndex);
        if (prevStepIndex === null) {
          return null; // No more steps
        }

        stepIndex = prevStepIndex;
        const prevStep = WIZARD_STEPS[stepIndex];

        // If previous step has sub-steps, start from last sub-step
        if (prevStep.subSteps) {
          subStepIndex = prevStep.subSteps.length - 1;
          const lastSubStep = prevStep.subSteps[subStepIndex];

          // Check if this sub-step should be skipped
          if (!lastSubStep.shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
            stepsSkipped++;
            if (stepsSkipped >= skipCount) {
              return { stepIndex, subStepIndex };
            }
          }
        } else {
          subStepIndex = 0;

          // Check if this main step should be skipped
          if (!prevStep.shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
            stepsSkipped++;
            if (stepsSkipped >= skipCount) {
              return { stepIndex, subStepIndex: undefined };
            }
          }
        }
      }

      return null;
    },
    [profileFieldConstraintsMap],
  );

  // Navigate to next step with optional skip count
  const goToNextStep = useCallback(
    (skipSteps: number = 1) => {
      const result = findNextValidStep(currentStepIndex, currentSubStepIndex, skipSteps);

      if (result) {
        const currentStepKey = WIZARD_STEPS[currentStepIndex]?.key as StepKey | undefined;

        if (currentStepKey) {
          dispatch(addVisitedWizardStep(currentStepKey));
        }

        const route = getRouteForNavigation(result.stepIndex, result.subStepIndex);
        navigate(route);
      }
    },
    [dispatch, navigate, currentStepIndex, currentSubStepIndex, findNextValidStep],
  );

  // Navigate to previous step with optional skip count
  const goToPreviousStep = useCallback(
    (skipSteps: number = 1) => {
      const result = findPreviousValidStep(currentStepIndex, currentSubStepIndex, skipSteps);

      if (result) {
        const route = getRouteForNavigation(result.stepIndex, result.subStepIndex);
        navigate(route);
      }
    },
    [navigate, currentStepIndex, currentSubStepIndex, findPreviousValidStep],
  );

  // Event handler versions for onClick (ignores the event parameter)
  const handleNextStep = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault?.();
      goToNextStep();
    },
    [goToNextStep],
  );

  const handlePreviousStep = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault?.();
      goToPreviousStep();
    },
    [goToPreviousStep],
  );

  // Direct navigation to any step/sub-step
  const navigateToStep = useCallback(
    (stepIndex: number, subStepIndex?: number) => {
      if (isValidStepIndex(stepIndex)) {
        const step = WIZARD_STEPS[stepIndex];

        // Validate sub-step index if provided
        if (subStepIndex !== undefined && step.subSteps && !isValidSubStepIndex(stepIndex, subStepIndex)) {
          console.warn(`Invalid sub-step index ${subStepIndex} for step ${stepIndex}`);
          return;
        }

        const route = getRouteForNavigation(stepIndex, subStepIndex);
        navigate(route);
      } else {
        console.warn(`Invalid step index: ${stepIndex}`);
      }
    },
    [navigate],
  );

  // Skip to a specific step by key
  const navigateToStepByKey = useCallback(
    (stepKey: string, subStepKey?: string) => {
      const stepIndex = WIZARD_STEPS.findIndex(step => step.key === stepKey);

      if (stepIndex === -1) {
        console.warn(`Step with key "${stepKey}" not found`);
        return;
      }

      let subStepIndex: number | undefined;

      if (subStepKey) {
        const step = WIZARD_STEPS[stepIndex];
        if (step.subSteps) {
          subStepIndex = step.subSteps.findIndex(subStep => subStep.key === subStepKey);
          if (subStepIndex === -1) {
            console.warn(`Sub-step with key "${subStepKey}" not found in step "${stepKey}"`);
            return;
          }
        }
      }

      navigateToStep(stepIndex, subStepIndex);
    },
    [navigateToStep],
  );

  // Check if navigation is possible
  const canGoToNextStep = findNextValidStep(currentStepIndex, currentSubStepIndex, 1) !== null;
  const canGoToPreviousStep = findPreviousValidStep(currentStepIndex, currentSubStepIndex, 1) !== null;

  // Get the name of the next valid step for button label
  const getNextStepName = useCallback(() => {
    const nextStep = findNextValidStep(currentStepIndex, currentSubStepIndex, 1);
    if (!nextStep) return null;

    const step = WIZARD_STEPS[nextStep.stepIndex];

    // If it has sub-steps and we're going to a specific sub-step, use that name
    if (step.subSteps && nextStep.subStepIndex !== undefined) {
      return step.subSteps[nextStep.subStepIndex].defaultTitle;
    }

    // Otherwise use the main step name
    return step.defaultTitle;
  }, [currentStepIndex, currentSubStepIndex, findNextValidStep]);

  return {
    currentStepIndex,
    currentSubStepIndex,
    goToNextStep,
    goToPreviousStep,
    handleNextStep,
    handlePreviousStep,
    navigateToStep,
    navigateToStepByKey,
    canGoToNextStep,
    canGoToPreviousStep,
    getNextStepName,
  };
};
